import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ATT_CODE, ATT_CLS } from "@/components/Badges";
import { api, MONTHS, DEPT, formatErr } from "@/lib/api";

const OPTIONS = [["present", "P"], ["half_day", "HD"], ["on_leave", "L"], ["absent", "A"]];

const Cell = ({ code, date, onMark, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button data-testid={testid} className={`h-7 w-8 rounded text-[10px] font-bold transition-transform hover:scale-110 ${code ? ATT_CLS[code] : "text-slate-300"}`}>{code || "·"}</button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-2 bg-white">
      <div className="text-[10px] text-slate-500 mb-1.5">{date}</div>
      <div className="flex gap-1">
        {OPTIONS.map(([s, c]) => (
          <button key={s} onClick={() => onMark(s)} className={`h-8 w-9 rounded text-xs font-bold ${ATT_CLS[c]}`} data-testid={`mark-${s}`}>{c}</button>
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

export default function Attendance() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [d, setD] = useState(null);

  const load = () => api.get("/attendance/register", { params: { month, year } }).then((r) => setD(r.data));
  useEffect(() => {
    load();
  }, [month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const map = useMemo(() => {
    const m = {};
    (d?.records || []).forEach((r) => { m[`${r.user_id}|${r.work_date}`] = r.status; });
    return m;
  }, [d]);

  const mark = async (user_id, work_date, status) => {
    try {
      await api.put("/attendance/mark", { user_id, work_date, status });
      toast.success("Attendance updated");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const days = d ? Array.from({ length: d.days }, (_, i) => i + 1) : [];
  const ds = (day) => `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl font-extrabold text-slate-900 tracking-tight">Attendance Register</h1>
          <p className="text-sm text-slate-500 mt-1">Past days with no record count as Absent. Click any cell to correct it.</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-36 bg-white" data-testid="attendance-month-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24 bg-white" data-testid="attendance-year-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{[year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-3 text-xs">{Object.entries(ATT_CLS).map(([k, c]) => <span key={k} className={`px-2 py-1 rounded font-bold ${c}`}>{k} · {{ P: "Present", A: "Absent", HD: "Half Day", L: "Leave" }[k]}</span>)}</div>
      <div className="bg-white rounded-2xl border overflow-x-auto" data-testid="attendance-register-grid">
        <table className="text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50">
              <th className="sticky left-0 bg-slate-50 text-left px-4 py-2 min-w-[200px] font-semibold text-slate-700">Staff</th>
              {days.map((day) => {
                const wd = new Date(year, month - 1, day).getDay();
                return <th key={day} className={`px-0.5 py-2 text-[10px] font-semibold ${wd === 0 ? "text-rose-500" : "text-slate-500"}`}>{day}</th>;
              })}
              <th className="px-3 text-[10px] text-slate-500">Worked</th>
            </tr>
          </thead>
          <tbody>
            {d?.users.map((u) => {
              let worked = 0;
              return (
                <tr key={u.id} className="border-t" data-testid={`attendance-row-${u.id}`}>
                  <td className="sticky left-0 bg-white px-4 py-1.5">
                    <div className="font-semibold text-slate-900 text-[13px] truncate max-w-[200px]">{u.full_name}</div>
                    <div className="text-[10px] text-slate-500">{DEPT[u.department]}</div>
                  </td>
                  {days.map((day) => {
                    const date = ds(day);
                    const st = map[`${u.id}|${date}`];
                    const code = st ? ATT_CODE[st] : date < d.today ? "A" : "";
                    if (code === "P") worked += 1;
                    if (code === "HD") worked += 0.5;
                    return <td key={day} className="px-0.5 py-1"><Cell code={code} date={date} onMark={(s) => mark(u.id, date, s)} testid={`att-cell-${u.id}-${day}`} /></td>;
                  })}
                  <td className="px-3 font-mono-num font-semibold text-slate-800">{worked}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
